<script setup lang="ts">
import { isHideCombosActiveKey } from '~/lib/interfaces/symbols'
import { PromotionType } from '~/lib/services/promos/promos.dto'
import { CartProduct } from '~/lib/services/store/catalog/cart.dtos'

const props = defineProps<{
  product: CartProduct
  cartIndex: number
  limitProductOrder: number
  mobile: boolean
}>()
const emit = defineEmits(['delete-product', 'update-units', 'click-navigate'])
const { format } = useFormatAmount()
const { t } = useLocale()
const { translateInterpolated } = useTranslation()
const { editProductRoute } = useMcdRoutes()
const { userSegmentation } = useProductPromos()
const appStore = useAppStore()
const isHideCombosActive = inject(isHideCombosActiveKey)!

const unit = ref(props.product.unit)

const validPromo = computed(() => {
  const isValidPromo = validPromoForBubble(props.product.promotions, userSegmentation.value) || null
  return !!isValidPromo && isValidPromo.active
})

const finalPriceProduct = computed(() => {
  const { computedPrice, isPromo } = props.product
  return validPromo.value || isPromo
    ? computedPrice.priceWithDiscountByUnit || 0
    : computedPrice.priceUnit
})

const customization = computed(() => {
  if (!props.product.customization) return undefined
  return translateInterpolated(props.product.customization)
})

const showCollapseIcon = computed(() => {
  if (!customization.value) return false
  const maxLength = props.mobile ? 30 : 40
  return customization.value.length > maxLength
})

const hasFreeShippingPromo = computed(
  () =>
    !!props.product.promotions?.some(
      promo => promo.type === PromotionType.SHIPPING_PERCENTAGE && promo.amount === 100
    )
)

const productName = computed(() => {
  if (isHideCombosActive.value) {
    return props.product.genericName ?? props.product.name
  }
  return props.product.name
})

const isAddDisabledByLimit = computed(() => {
  const found = appStore.cartCatalogProducts.find(
    product => product.identifier === props.product.identifier
  )
  const limit = found?.limitProduct ?? null
  if (limit === null) return false
  return unit.value >= limit
})

const updateCartProductUnits = (currUnit: number) => {
  const isAdd = currUnit > unit.value
  unit.value = currUnit
  emit('update-units', { unit: unit.value, index: props.cartIndex, isAdd })
}
</script>

<template>
  <!-- !mobile: cart item on desktop -->
  <div v-if="!mobile" data-test="cart-item" class="my-4 flex flex-row gap-4">
    <ImageResolver class="h-14 w-14" :url="product.image" :alt="productName" />
    <div class="w-full">
      <!-- name, price -->
      <div class="mb-2 flex flex-row justify-between">
        <p class="line-clamp-1 max-w-[70%] text-sm">
          {{ productName }}
        </p>
        <div class="flex flex-row gap-1 text-sm">
          <p
            v-if="validPromo || product.isPromo"
            data-test="cart-item__price--promoted"
            class="line-through"
          >
            {{ format(product.computedPrice.priceUnit) }}
          </p>
          <p data-test="cart-item__price" class="font-bold">
            {{ format(finalPriceProduct) }}
          </p>
        </div>
      </div>
      <div class="mb-2 flex flex-row items-center justify-between gap-4">
        <!-- Collapsable customization -->
        <BasicCollapsable
          v-if="customization"
          class="max-w-[65%] flex-1 text-xs text-mcd-secondaryDarkGrey"
          :show-collapse="showCollapseIcon"
        >
          <template #header>
            <p v-dompurify-html="customization" class="line-clamp-1 max-w-full"></p>
          </template>
          <template #content>
            <p v-dompurify-html="customization" class="max-w-full"></p>
          </template>
        </BasicCollapsable>
        <!-- Free shipping bubble -->
        <FreeShippingBubble v-if="hasFreeShippingPromo" class="ml-auto" big />
      </div>
      <!-- Edit link, quantity selector -->
      <div class="flex flex-row items-center justify-between">
        <NuxtLink
          data-test="cart-item__edit"
          class="flex cursor-pointer flex-row items-center gap-2"
          :to="editProductRoute(cartIndex)"
          @click.prevent="emit('click-navigate')"
        >
          <span class="block text-[10px] text-xs/4 text-mcd-secondaryBlue underline">
            {{ t('shoppingCart.actions.edit') }}
          </span>
          <Chevron class="h-4 w-4" fill="#006BAE" />
        </NuxtLink>
        <QuantitySelector
          :min="1"
          :max="limitProductOrder"
          :count="unit"
          :disable-add="(product.isPromo ? product.limitReached : false) || isAddDisabledByLimit"
          :disable-substract="product.isPromo ? product.limitReached : false"
          :show-always-delete="product.isPromo"
          @update:count="updateCartProductUnits"
          @delete="$emit('delete-product', cartIndex)"
        />
      </div>
    </div>
  </div>
  <!-- mobile: CartOverlay, cart item on desktop -->
  <section v-else data-test="cart-item" class="my-4">
    <div class="mb-2 flex flex-row gap-4">
      <ImageResolver class="h-14 w-14" :url="product.image" :alt="productName" />
      <div class="w-full">
        <!-- name, price -->
        <div class="mb-2 flex flex-row justify-between">
          <p class="line-clamp-1 max-w-[70%] text-sm">
            {{ productName }}
          </p>
          <div class="flex flex-row gap-1 text-sm">
            <p
              v-if="validPromo || product.isPromo"
              data-test="cart-item__price--promoted"
              class="line-through"
            >
              {{ format(product.computedPrice.priceUnit) }}
            </p>
            <p data-test="cart-item__price" class="font-bold">
              {{ format(finalPriceProduct) }}
            </p>
          </div>
        </div>

        <!-- Edit link, quantity selector -->
        <div class="flex flex-row items-center justify-between">
          <NuxtLink
            data-test="cart-item__edit"
            class="flex cursor-pointer flex-row items-center gap-2 rounded px-2 py-1 shadow-elevation-down"
            :to="editProductRoute(cartIndex)"
            @click.prevent="emit('click-navigate')"
          >
            <span class="block text-2xs font-bold text-mcd-secondaryBlue">
              {{ t('shoppingCart.actions.edit') }}
            </span>
            <ArrowRight class="h-4 w-4" />
          </NuxtLink>

          <!-- Free shipping bubble -->
          <FreeShippingBubble v-if="hasFreeShippingPromo" big />
        </div>
      </div>
    </div>
    <div class="flex flex-row items-center gap-4">
      <!-- Collapsable customization -->
      <div class="w-full text-xs text-mcd-secondaryDarkGrey">
        <BasicCollapsable v-if="customization" :show-collapse="showCollapseIcon">
          <template #header>
            <p v-dompurify-html="customization" class="line-clamp-2 max-w-[80%] md:max-w-full"></p>
          </template>
          <template #content>
            <p v-dompurify-html="customization" class="max-w-[80%] md:max-w-full"></p>
          </template>
        </BasicCollapsable>
      </div>
      <!-- QuantitySelector -->
      <QuantitySelector
        :min="1"
        :max="limitProductOrder"
        :count="unit"
        :disable-add="(product.isPromo ? product.limitReached : false) || isAddDisabledByLimit"
        :disable-substract="product.isPromo ? product.limitReached : false"
        :show-always-delete="product.isPromo"
        @update:count="updateCartProductUnits"
        @delete="$emit('delete-product', cartIndex)"
      />
    </div>
  </section>
</template>
